import { Object, Array, String, Static, Optional, Boolean, Number, Null, Union, Literal } from '@sinclair/typebox';

import { DapiTravelDocuments, DapiRetrieveReservationBound, DapiSpecialServiceRequests } from '../dapi';

export const ConfirmationContact = Object({
  id: String(),
  travelerIds: Optional(Array(String())),
  category: String(),
  contactType: String(),
  deviceType: Optional(String()),
  purpose: String(),
  countryPhoneExtension: Optional(String()),
  number: Optional(String()),
  address: Optional(String()),
  freeFlowText: Optional(String()),
});
export type ConfirmationContact = Static<typeof ConfirmationContact>;

const Name = Object({
  firstName: String(),
  lastName: String(),
  title: String(),
  nameType: String(),
  isPreferred: Boolean(),
});
type Name = Static<typeof Name>;

export const ConfirmationTraveler = Object({
  passengerTypeCode: String(),
  id: String(),
  names: Array(Name),
  accompanyingTravelerId: Optional(String()),
  dateOfBirth: Optional(String()),
});
export type ConfirmationTraveler = Static<typeof ConfirmationTraveler>;

const CodeName = Object({ code: String(), name: String() });
type CodeName = Static<typeof CodeName>;

export const ConfirmationFlightSegment = Object({
  segmentId: String(),
  departureTerminal: Optional(String()),
  arrivalTerminal: Optional(String()),
  arrivalDateTimeInLocal: String(),
  arrivalDateTimeInGmt: String(),
  departureDateTimeInLocal: String(),
  departureDateTimeInGmt: String(),
  startTimeInLocal: String(),
  endTimeInLocal: String(),
  departureAirport: CodeName,
  arrivalAirport: CodeName,
  departureCity: CodeName,
  arrivalCity: CodeName,
  airCraft: CodeName,
  flightNumber: String(),
  bookingClass: String(),
  marketingCarrier: CodeName,
  operatingCarrier: CodeName,
  duration: String(),
  iswetlease: Boolean(),
});

export type ConfirmationFlightSegment = Static<typeof ConfirmationFlightSegment>;

export const ConfirmationBoundFlightSummary = Object({
  flightKey: String(),
  id: Number(),
  boundType: Union([Literal('OUTBOUND'), Literal('INBOUND')]),
  stops: Number(),
  origin: CodeName,
  originCity: CodeName,
  originCountry: CodeName,
  destination: CodeName,
  destinationCity: CodeName,
  destinationCountry: CodeName,
  startDateTimeInGmt: String(),
  startTimeInLocal: String(),
  startDateTimeInLocal: String(),
  endDateTimeInGmt: String(),
  endTimeInLocal: String(),
  endDateTimeInLocal: String(),
  connectionDuration: String(),
  segments: Array(ConfirmationFlightSegment),
  cabins: Optional(Array(String())),
});

export type ConfirmationBoundFlightSummary = Static<typeof ConfirmationBoundFlightSummary>;

const Service = Object({
  id: String(),
  descriptions: Array(Object({ type: String(), content: String() })),
  quantity: Number(),
  flightIds: Array(String()),
  statusCode: String(),
  parameters: Array(Object({ code: String(), value: String() })),
  travelerId: String(),
  isChargeable: Boolean(),
});
type Service = Static<typeof Service>;

export const ConfirmationFrequentFlyerCard = Object({
  id: String(),
  companyCode: String(),
  cardNumber: String(),
  travelerId: String(),
  tierLevel: String(),
  priorityCode: String(),
});
export type ConfirmationFrequentFlyerCard = Static<typeof ConfirmationFrequentFlyerCard>;

export const ConfirmationOrder = Object({
  id: String(),
  creationPointOfSale: Object({
    PointOfSaleId: String(),
    countryCode: String(),
  }),
  creationDateTime: String(),
  lastModificationDateTime: String(),
  isGroupBooking: Boolean(),
  air: Object({
    bounds: Array(DapiRetrieveReservationBound),
  }),
  services: Array(Service),
  travelers: Array(ConfirmationTraveler),
  contacts: Array(ConfirmationContact),
  frequentFlyerCards: Array(ConfirmationFrequentFlyerCard),
  travelDocuments: Array(DapiTravelDocuments),
  remarks: Array(Object({ id: String(), remarkType: String(), freeText: String() })),
  specialServiceRequests: Array(DapiSpecialServiceRequests),
});
export type ConfirmationOrder = Static<typeof ConfirmationOrder>;

export namespace SelfReaccommodationEmailNS {
  export type Segment = Static<typeof Segment>;
  export type FlightSummary = Static<typeof FlightSummary>;
  export type Passenger = Static<typeof Passenger>;
  export type Link = Static<typeof Link>;
  export type Payload = Static<typeof Payload>;

  export type StringifiedPayload = Payload & {
    outboundSummary: string;
    outbound: string;
    inboundSummary?: string;
    passengers: string;
    links: string;
  };
}

const Segment = Object({
  duration: String(),
  transitTime: Union([String(), Null()]),
  departureDateLocal: String(),
  departureTimeLocal: String(),
  departureCountryCode: Union([String(), Null()]),
  departureCityName: String(),
  departureAirportName: String(),
  departureAirportCode: String(),
  departureTerminal: Union([Number(), Null()]),
  arrivalDateLocal: String(),
  arrivalTimeLocal: String(),
  arrivalCountryCode: Union([String(), Null()]),
  arrivalCityName: String(),
  arrivalAirportName: String(),
  arrivalAirportCode: String(),
  arrivalTerminal: Union([Number(), Null()]),
  operatingCarrier: String(),
  bookingClass: String(),
  flightNumber: String(),
  ancillaries: Optional(Array(Object({ text: String(), code: String(), status: String() }))),
});

const FlightSummary = Object({
  duration: String(),
  numberOfStops: Number(),
  originCountryCode: String(),
  originAirportCode: String(),
  originCityName: String(),
  startDateLocal: String(),
  startTimeLocal: String(),
  destinationCountryCode: String(),
  destinationAirportCode: String(),
  destinationCityName: String(),
  endDateLocal: String(),
  endTimeLocal: String(),
});

const Passenger = Object({
  id: String(),
  type: String(),
  firstName: String(),
  lastName: String(),
  email: String(),
  mobile: String(),
  frequentFlyerNumber: String(),
  hasInfant: Boolean(),
  isPrimary: Boolean(),
});

const Link = Object({
  code: String(),
  url: String(),
});

const Payload = Object({
  correlationId: String(),
  pos: String(),
  pnr: String(),
  countryCode: String(),
  languageCode: String(),
  isOneway: Boolean(),
  outboundSummary: Optional(FlightSummary),
  outbound: Optional(Object({ segments: Array(Segment) })),
  inboundSummary: Optional(FlightSummary),
  inbound: Optional(Object({ segments: Array(Segment) })),
  passengers: Array(Passenger),
  links: Array(Link),
  requestDateUTC: String(),
});
